import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Webcam from 'react-webcam'
import { CaretLeft, Camera } from 'phosphor-react'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { FlexRow, FlexRowBetween, PageHeader } from 'components'
import { DesktopMode, MobileMode } from 'layout'
import styled from 'styled-components'
import { useAsset, useAssetActivity } from 'hooks/asset-manager'
import { parseAssetQrPayload } from 'utility/asset-qr'
import { useAuth } from 'context/user'
import { getAssetPermissions } from 'utility/asset-permissions'

const ScanContainer = styled.div`
    padding: 20px;
`

export const AssetScanScreen = () => {
    const navigate = useNavigate()
    const webcamRef = useRef(null)
    const lastScanRef = useRef('')
    const lastAtRef = useRef(0)
    const detectorRef = useRef(null)
    const [isScanning, setIsScanning] = useState(true)
    const [manualCode, setManualCode] = useState('')
    const [scanMessage, setScanMessage] = useState('')
    const [isOnline, setIsOnline] = useState(
        typeof navigator !== 'undefined' ? navigator.onLine : true,
    )
    const { user } = useAuth()
    const permissions = getAssetPermissions(user)
    const { captureActivity } = useAssetActivity()
    const { assets, fetchAssets } = useAsset()

    useEffect(() => {
        if ('BarcodeDetector' in window) {
            detectorRef.current = new window.BarcodeDetector({
                formats: ['qr_code'],
            })
        }
    }, [])

    useEffect(() => {
        fetchAssets({ OnlyActive: true })
    }, [fetchAssets])

    useEffect(() => {
        const online = () => setIsOnline(true)
        const offline = () => setIsOnline(false)
        window.addEventListener('online', online)
        window.addEventListener('offline', offline)
        return () => {
            window.removeEventListener('online', online)
            window.removeEventListener('offline', offline)
        }
    }, [])

    const assetsForUi = useMemo(
        () => (Array.isArray(assets) ? assets : []),
        [assets],
    )

    const resolveAssetFromCode = rawCode => {
        const parsed = parseAssetQrPayload(rawCode)
        const codeId = String(parsed.assetId || '').trim()
        const codeTag = String(parsed.assetTag || rawCode || '')
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '')

        return (
            assetsForUi.find(asset => {
                const id = String(asset.AssetId || '').trim()
                const tag = String(asset.AssetTag || '')
                    .toLowerCase()
                    .replace(/[^a-z0-9]/g, '')
                return (
                    (codeId && id === codeId) ||
                    (codeTag && tag === codeTag)
                )
            }) || null
        )
    }

    const processScanCode = rawCode => {
        if (!permissions.canUseScanner) return
        const now = Date.now()
        if (lastScanRef.current === rawCode && now - lastAtRef.current < 1800) {
            return
        }
        lastScanRef.current = rawCode
        lastAtRef.current = now

        if (!isOnline) {
            setScanMessage(
                'Scanning requires internet connection until backend offline sync API is available.',
            )
            return
        }

        const asset = resolveAssetFromCode(rawCode)
        if (!asset) {
            setScanMessage('No asset found for scanned code.')
            return
        }
        captureActivity({
            assetId: asset.AssetId || '',
            action: 'Asset opened from QR scan',
        })
        setScanMessage(`Opening ${asset.AssetName || asset.assetName}...`)
        navigate(`/app/asset-management/${asset.AssetId || asset.AssetTag}`, {
            state: { asset },
        })
    }

    useEffect(() => {
        if (!isScanning || !detectorRef.current || !webcamRef.current) {
            return undefined
        }
        const timer = setInterval(async () => {
            try {
                const video = webcamRef.current?.video
                if (!video || video.readyState < 2) return
                const codes = await detectorRef.current.detect(video)
                if (codes && codes.length > 0 && codes[0].rawValue) {
                    processScanCode(codes[0].rawValue)
                }
            } catch {
                // keep silent to avoid console noise on unsupported frames
            }
        }, 1200)
        return () => clearInterval(timer)
    }, [isScanning, isOnline, assetsForUi])

    return (
        <ScanContainer>
            <DesktopMode>
                <FlexRowBetween className="mb-3">
                    <FlexRow className="align-items-center">
                        <FlexRow
                            onClick={() => navigate('/app/asset-management')}
                            className="align-items-center pointer me-3"
                        >
                            <CaretLeft size={24} />
                            <div className="ms-2">Back</div>
                        </FlexRow>
                        <PageHeader header="Scan Asset QR" />
                    </FlexRow>
                    <div className="d-flex gap-2">
                        <AppSecondaryButton
                            type="button"
                            onClick={() => setIsScanning(s => !s)}
                        disabled={!permissions.canUseScanner}
                        >
                            {isScanning ? 'Pause Scanner' : 'Resume Scanner'}
                        </AppSecondaryButton>
                    </div>
                </FlexRowBetween>
            </DesktopMode>

            <MobileMode>
                <FlexRowBetween className="mb-3">
                    <FlexRow
                        onClick={() => navigate('/app/asset-management')}
                        className="align-items-center pointer"
                    >
                        <CaretLeft size={24} />
                    </FlexRow>
                    <div style={{ fontSize: 20, fontWeight: 600 }}>Scan Asset QR</div>
                </FlexRowBetween>
            </MobileMode>

            <div
                style={{
                    border: '1px solid #d1d5db',
                    borderRadius: 10,
                    overflow: 'hidden',
                    maxWidth: 560,
                }}
            >
                {detectorRef.current ? (
                    <Webcam
                        ref={webcamRef}
                        audio={false}
                        screenshotFormat="image/jpeg"
                        videoConstraints={{ facingMode: 'environment' }}
                        style={{ width: '100%', display: 'block' }}
                    />
                ) : (
                    <div
                        style={{
                            minHeight: 260,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 10,
                            color: '#64748b',
                        }}
                    >
                        <Camera size={20} />
                        QR auto-detection not supported on this browser.
                    </div>
                )}
            </div>

            <div className="mt-3" style={{ maxWidth: 560 }}>
                <FormFieldLabel>Manual Code Input</FormFieldLabel>
                <div className="d-flex gap-2">
                    <input
                        className="form-control"
                        value={manualCode}
                        onChange={e => setManualCode(e.target.value)}
                        placeholder="Paste/enter QR code payload"
                    />
                    <AppPrimaryButton
                        type="button"
                        onClick={() => processScanCode(manualCode)}
                        disabled={!manualCode.trim() || !permissions.canUseScanner}
                    >
                        Open
                    </AppPrimaryButton>
                </div>
            </div>

            <div className="mt-3" style={{ maxWidth: 560 }}>
                <div className="d-flex align-items-center gap-2">
                    <span style={{ color: '#475569' }}>
                        {isOnline ? 'Online' : 'Offline'}
                    </span>
                </div>
                {scanMessage && (
                    <div
                        className="mt-2"
                        style={{ fontSize: 14, color: '#334155' }}
                    >
                        {scanMessage}
                    </div>
                )}
            </div>
        </ScanContainer>
    )
}

const FormFieldLabel = ({ children }) => (
    <div style={{ fontWeight: 600, marginBottom: 6 }}>{children}</div>
)
